import {
  Button,
  ButtonProps,
  InteractiveContainer,
  PlainButton,
  Switch,
  SwitchProps,
  Menu,
  MenuSections,
} from "@jugl-web/ui-kit";
import { cx } from "@jugl-web/utils";
import React from "react";
import { CRMStatus, CRMStatusPill } from "../CRMStatusPill";
import { ReactComponent as MenuIcon } from "./assets/menu.svg";

export interface CRMAccountCardProps {
  title: string;
  description?: React.ReactNode;
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  children?: React.ReactNode;
  status?: CRMStatus;
  img?: string;
  buttonProps?: Omit<ButtonProps, "ref"> & {
    text: string;
  };
  switchProps?: SwitchProps;
  menuItems?: MenuSections;
  onClick?: () => void;
}

export const CRMAccountCard: React.FC<CRMAccountCardProps> = ({
  img,
  title,
  description,
  status,
  icon,
  buttonProps,
  switchProps,
  menuItems,
  onClick,
  children,
}) => (
  <InteractiveContainer
    className={cx(
      "flex flex-col gap-6 rounded-3xl border border-solid border-dark-100 bg-white p-8 shadow-[0_2px_4px_rgba(0,0,0,0.08)] transition-shadow max-lg:gap-4 max-lg:p-5",
      onClick ? "hover:shadow-[0_8px_16px_rgba(0,0,0,0.08)]" : "cursor-default"
    )}
    onClick={onClick}
  >
    <div className="flex items-center justify-between gap-8 max-lg:flex-wrap max-lg:gap-3">
      <div className="flex w-full min-w-0 items-center gap-6 max-lg:gap-3">
        {img ? (
          <div className="relative">
            <div className="h-[72px] w-[72px] shrink-0 overflow-hidden rounded-2xl border border-solid border-[#E0E0E0] bg-[#E0E0E0]">
              <img
                src={img}
                alt={title}
                className="h-full w-full object-cover object-center"
              />
            </div>
            <div className="absolute -right-2.5 -bottom-2.5 h-10 w-10">
              {React.createElement(icon, { className: "w-full h-full" })}
            </div>
          </div>
        ) : (
          <>{React.createElement(icon, { className: "shrink-0" })}</>
        )}
        <div className="flex min-w-0 flex-col gap-1">
          <span className="font-secondary text-xl leading-5 font-medium break-words max-lg:text-base">
            {title}
          </span>
          {description && (
            <span className="font-secondary text-sm leading-[140%] text-dark-700">
              {description}
            </span>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-4">
        {status && <CRMStatusPill status={status} />}
        {buttonProps && (
          <>
            <div className="mx-1 h-10 w-px shrink-0 bg-dark-100" />
            <Button className="h-10" {...buttonProps}>
              {buttonProps.text}
            </Button>
          </>
        )}
        {switchProps && (
          <div onClick={(e) => e.stopPropagation()}>
            <Switch {...switchProps} />
          </div>
        )}
        {menuItems && (
          <Menu
            placement="bottom"
            renderTrigger={({ Trigger, triggerRef }) => (
              <Trigger
                ref={triggerRef}
                as={PlainButton}
                className="flex h-10 w-10 items-center justify-center rounded-lg bg-grey-100 transition-colors hover:bg-grey-200"
                onClick={(e) => e.stopPropagation()}
              >
                <MenuIcon />
              </Trigger>
            )}
            autoClose
            sections={menuItems}
          />
        )}
      </div>
    </div>
    {children}
  </InteractiveContainer>
);
